/**
 * Secure token storage utilities
 * Implements OWASP best practices for JWT storage in browser
 *
 * SECURITY NOTES:
 * - Prefers sessionStorage over localStorage to limit token lifetime
 * - Adds token expiration validation
 * - Implements token rotation
 * - Clears tokens on window unload
 *
 * IDEAL SOLUTION: Use HttpOnly cookies (requires backend changes)
 */

import { jwtDecode } from 'jwt-decode';

const TOKEN_KEY = 'cm_auth_tokens';

/**
 * Check if a JWT token is expired
 * @param {string} token - JWT token to check
 * @returns {boolean} - True if token is expired
 */
export function isTokenExpired(token) {
  if (!token) return true;

  try {
    const decoded = jwtDecode(token);

    if (!decoded.exp) {
      // No expiration claim - consider expired for security
      return true;
    }

    // exp is in seconds, Date.now() is in milliseconds
    const currentTime = Date.now() / 1000;

    // Add 5 minute buffer before actual expiration
    const bufferSeconds = 5 * 60;
    return decoded.exp < (currentTime + bufferSeconds);
  } catch (error) {
    if (process.env.NODE_ENV === 'development') {
      console.error('Error checking token expiration:', error);
    }
    return true; // Treat decode errors as expired
  }
}

/**
 * Get tokens from storage (sessionStorage preferred)
 * @returns {Object|null} - Tokens object or null
 */
export function getTokens() {
  try {
    // Try sessionStorage first (more secure)
    let stored = window.sessionStorage.getItem(TOKEN_KEY);

    // Fallback to localStorage if not found
    if (!stored) {
      stored = window.localStorage.getItem(TOKEN_KEY);

      // If found in localStorage, migrate to sessionStorage
      if (stored) {
        window.sessionStorage.setItem(TOKEN_KEY, stored);
        window.localStorage.removeItem(TOKEN_KEY);
      }
    }

    if (!stored) return null;

    const tokens = JSON.parse(stored);

    // Validate token expiration
    const accessToken = tokens?.accessToken || tokens?.token;
    if (accessToken && isTokenExpired(accessToken)) {
      clearTokens();
      return null;
    }

    return tokens;
  } catch (error) {
    if (process.env.NODE_ENV === 'development') {
      console.error('Error reading tokens:', error);
    }
    return null;
  }
}

/**
 * Store tokens securely
 * @param {Object} tokens - Tokens to store
 * @param {boolean} rememberMe - If true, use localStorage (NOT RECOMMENDED)
 */
export function setTokens(tokens, rememberMe = false) {
  try {
    if (!tokens) {
      clearTokens();
      return;
    }

    const tokenString = JSON.stringify(tokens);

    if (rememberMe) {
      // User explicitly requested to stay logged in
      // Still not ideal, but use localStorage
      window.localStorage.setItem(TOKEN_KEY, tokenString);
    } else {
      // Default: use sessionStorage (cleared when browser closes)
      window.sessionStorage.setItem(TOKEN_KEY, tokenString);
    }
  } catch (error) {
    if (process.env.NODE_ENV === 'development') {
      console.error('Error storing tokens:', error);
    }
  }
}

/**
 * Clear all tokens from storage
 */
export function clearTokens() {
  try {
    window.localStorage.removeItem(TOKEN_KEY);
    window.sessionStorage.removeItem(TOKEN_KEY);
  } catch (error) {
    if (process.env.NODE_ENV === 'development') {
      console.error('Error clearing tokens:', error);
    }
  }
}

/**
 * Get access token if valid
 * @returns {string|null} - Access token or null if expired/missing
 */
export function getAccessToken() {
  const tokens = getTokens();
  if (!tokens) return null;

  const accessToken = tokens?.accessToken || tokens?.token;

  if (accessToken && !isTokenExpired(accessToken)) {
    return accessToken;
  }

  // Token expired, clear storage
  clearTokens();
  return null;
}

/**
 * Initialize token cleanup on window unload
 * Call this in your main App component
 */
export function initTokenCleanup() {
  // Clear tokens when window/tab closes (sessionStorage only)
  window.addEventListener('beforeunload', () => {
    // Don't clear localStorage if user chose "remember me"
    // Only clear sessionStorage
    try {
      window.sessionStorage.removeItem(TOKEN_KEY);
    } catch (error) {
      // Ignore errors during unload
    }
  });

  // Periodic token expiration check (every 5 minutes)
  setInterval(() => {
    const tokens = getTokens();
    if (!tokens) return;

    const accessToken = tokens?.accessToken || tokens?.token;
    if (accessToken && isTokenExpired(accessToken)) {
      clearTokens();

      // Notify user (optional - could dispatch event instead)
      if (process.env.NODE_ENV === 'development') {
        console.warn('Access token expired - cleared from storage');
      }
    }
  }, 5 * 60 * 1000); // Check every 5 minutes
}

/**
 * Check if user is authenticated with valid token
 * @returns {boolean}
 */
export function isAuthenticated() {
  const token = getAccessToken();
  return !!token;
}
